import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Home,
  Wrench,
  User,
  Lock,
  LogIn,
  Building2,
  ArrowRight,
  Settings,
  Save,
  Plus,
  X,
  Shield,
  Users,
  SlidersHorizontal,
} from 'lucide-react';

const env = typeof process !== 'undefined' ? process.env : {};
const LOGIN_API = env.REACT_APP_LOGIN_API || 'https://ahmsportal-n8n.zeabur.app/webhook/login-session';
const HEARTBEAT_API = env.REACT_APP_HEARTBEAT_API || 'https://ahmsportal-n8n.zeabur.app/webhook/heartbeat-session';
const LOGOUT_API = env.REACT_APP_LOGOUT_API || 'https://ahmsportal-n8n.zeabur.app/webhook/logout-session';
const ADMIN_DATA_API = env.REACT_APP_ADMIN_DATA_API || 'https://ahmsportal-n8n.zeabur.app/webhook/admin-data';
const SAVE_USER_API = env.REACT_APP_SAVE_USER_API || 'https://ahmsportal-n8n.zeabur.app/webhook/save-user';
const SYSTEM_SETTINGS_API = env.REACT_APP_SYSTEM_SETTINGS_API || 'https://ahmsportal-n8n.zeabur.app/webhook/system-settings';
const SAVE_SYSTEM_SETTINGS_API = env.REACT_APP_SAVE_SYSTEM_SETTINGS_API || 'https://ahmsportal-n8n.zeabur.app/webhook/save-system-settings';
const AHMShi_URL = env.REACT_APP_AHMShi_URL || 'https://goinfo-ahmshi.zeabur.app/';
const AHMShr_URL = env.REACT_APP_AHMShr_URL || 'https://goinfo-ahmshr.zeabur.app/';

function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (err) {
    return null;
  }
}

const EMPTY_FORM = {
  userId: '',
  name: '',
  password: '',
  permissions: [],
  deptCodes: [],
};

function VendorSettingsModal({
  visible,
  onClose,
  maxOnlineUsers,
  setMaxOnlineUsers,
  currentOnlineUsers,
  setCurrentOnlineUsers,
  vendorSuccess,
  vendorError,
  onSave,
  vendorLoading,
}) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[92vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col bg-[#1f2a3f] text-white">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[22px] font-bold">
              <Shield size={20} className="text-[#69a7ff]" />
              系統環境設定
            </div>
            <p className="text-sm text-white/60 mt-1">軟體公司維運後台：同時上線人數控管</p>
          </div>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/90 text-sm font-medium">關閉</button>
        </div>

        <div className="px-6 pt-4">
          {vendorError && <div className="mb-3 text-red-100 text-sm bg-red-500/20 border border-red-300/20 rounded-xl px-4 py-3">{vendorError}</div>}
          {vendorSuccess && <div className="mb-3 text-green-100 text-sm bg-green-500/20 border border-green-300/20 rounded-xl px-4 py-3">{vendorSuccess}</div>}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-5">
            <div className="bg-white/8 border border-white/10 rounded-3xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-white">最大同時登入人數上限</h3>
              <p className="text-white/60 text-sm mt-2 mb-4">超過此人數時，新登入者將被阻擋。</p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  value={maxOnlineUsers}
                  onChange={(e) => setMaxOnlineUsers(Number(e.target.value))}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-lg font-bold focus:outline-none focus:border-[#4f8cff] focus:ring-1 focus:ring-[#4f8cff] transition-colors"
                />
                <span className="text-white/80 font-medium whitespace-nowrap">人</span>
              </div>
            </div>

            <div className="bg-white/5 border border-dashed border-white/15 rounded-3xl p-6">
              <h3 className="text-xl font-bold text-white">目前線上人數</h3>
              <p className="text-white/60 text-sm mt-2 mb-4">可手動輸入模擬即時線上人數，方便測試登入阻擋邏輯。</p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  value={currentOnlineUsers}
                  onChange={(e) => setCurrentOnlineUsers(Number(e.target.value))}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-[#4ade80] text-lg font-bold focus:outline-none focus:border-[#22c55e] focus:ring-1 focus:ring-[#22c55e] transition-colors"
                />
                <span className="text-white/80 font-medium whitespace-nowrap">人</span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onSave}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#3b82f6] hover:bg-[#2563eb] text-white font-medium transition-colors"
              >
                <Save size={16} />
                {vendorLoading ? '儲存中...' : '儲存系統設定'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerPermissionModal({
  visible,
  onClose,
  adminLoading,
  adminSaving,
  adminError,
  adminSuccess,
  adminUsers,
  selectedUserId,
  setSelectedUserId,
  resetFormForNewUser,
  form,
  updateFormField,
  togglePermission,
  works,
  toggleDeptCode,
  handleSaveUser,
  currentOnlineUsers,
  maxOnlineUsers,
  onlineStatusMap,
}) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black/50 flex items-stretch justify-center p-2 sm:p-4">
      <div className="w-full max-w-6xl h-[calc(100vh-16px)] sm:h-[92vh] bg-[#1f2a3f] text-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-5 flex-shrink-0 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[22px] font-bold">
              <Users size={20} className="text-[#69a7ff]" />
              <span>權限設定</span>
            </div>
            <p className="text-sm text-white/60 mt-1">客戶 ROOT 後台：帳號與系統權限管理</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center rounded-full bg-[#69a7ff]/15 text-[#cfe1ff] px-3 py-1 text-xs font-bold tracking-wide">
                已上線 / 上限：{currentOnlineUsers}/{maxOnlineUsers}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/90 text-sm font-medium"
          >
            關閉
          </button>
        </div>

        <div className="px-6 pt-4 flex-shrink-0">
          {adminError && (
            <div className="mb-3 text-red-100 text-sm bg-red-500/20 border border-red-300/20 rounded-xl px-4 py-3">
              {adminError}
            </div>
          )}
          {adminSuccess && (
            <div className="mb-3 text-green-100 text-sm bg-green-500/20 border border-green-300/20 rounded-xl px-4 py-3">
              {adminSuccess}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 p-4 sm:p-6 flex flex-col">
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)] bg-white rounded-3xl text-gray-900 overflow-hidden shadow-inner">
            <div className="border-r border-gray-100 flex flex-col min-h-0 bg-gray-50/30">
              <div className="p-5 flex-shrink-0 border-b border-gray-100 bg-white">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-gray-800">使用者清單</h3>
                  </div>

                  <button
                    type="button"
                    onClick={resetFormForNewUser}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#eef3ff] text-[#2c63f0] hover:bg-[#dfe9ff] text-sm font-bold transition-colors shrink-0"
                  >
                    <Plus size={14} />
                    新增
                  </button>
                </div>
              </div>

              <div
                className="p-5 pr-3 flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-2"
                style={{ scrollbarGutter: 'stable' }}
              >
                {adminLoading ? (
                  <div className="text-sm text-gray-500 py-6 text-center">讀取中...</div>
                ) : adminUsers.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">目前沒有使用者資料</div>
                ) : (
                  adminUsers.map((item) => {
                    const active = item.userId === selectedUserId;
                    const normalizedUserId = String(item.userId || '').trim().toUpperCase();
                    const isOnline = Boolean(onlineStatusMap?.[normalizedUserId]);

                    return (
                      <button
                        key={item.userId}
                        type="button"
                        onClick={() => setSelectedUserId(item.userId)}
                        className={[
                          'w-full text-left rounded-2xl border px-4 py-3 transition-all',
                          active
                            ? 'border-[#cddaff] bg-[#eef3ff] shadow-sm'
                            : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[15px] font-bold text-gray-900 break-all">
                              {item.userId}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">{item.name}</div>
                          </div>

                          <span
                            className={[
                              'inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold border',
                              isOnline
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                : 'bg-gray-100 text-gray-500 border-gray-200',
                            ].join(' ')}
                          >
                            {isOnline ? '上線' : '離線'}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="min-h-0 flex flex-col">
              <div
                className="p-6 flex-1 min-h-0 overflow-y-auto overscroll-contain"
                style={{ scrollbarGutter: 'stable' }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#3b4c6b] mb-2">帳號</label>
                    <input
                      type="text"
                      value={form.userId}
                      onChange={(e) => updateFormField('userId', e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0]"
                      placeholder="例如 ROOT"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3b4c6b] mb-2">姓名</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateFormField('name', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0]"
                      placeholder="請輸入姓名"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-[#3b4c6b] mb-2">密碼</label>
                  <input
                    type="text"
                    value={form.password}
                    onChange={(e) => updateFormField('password', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0]"
                    placeholder="請輸入密碼"
                  />
                </div>

                <div className="mt-6">
                  <h3 className="text-base font-bold text-gray-800 mb-3">可使用系統</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {['AHMShi', 'AHMShr'].map((perm) => (
                      <label
                        key={perm}
                        className="flex items-center gap-3 rounded-2xl border border-gray-200 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(perm)}
                          onChange={() => togglePermission(perm)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-medium text-gray-700">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-base font-bold text-gray-800 mb-3">可查看建案</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-2">
                    {works.map((work) => (
                      <label
                        key={work.Workcode}
                        className="flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.deptCodes.includes(String(work.Workcode))}
                          onChange={() => toggleDeptCode(String(work.Workcode))}
                          className="w-4 h-4 mt-0.5"
                        />
                        <span className="text-sm text-gray-700 leading-relaxed">
                          <span className="font-bold text-gray-900">{String(work.Workcode)}</span> -{' '}
                          {String(work.Name || '')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 bg-white flex-shrink-0 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={resetFormForNewUser}
                  className="px-5 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                >
                  清空表單
                </button>
                <button
                  type="button"
                  onClick={handleSaveUser}
                  disabled={adminSaving}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#2c63f0] hover:bg-[#1e4ed8] text-white font-medium transition-colors disabled:opacity-70"
                >
                  <Save size={16} />
                  {adminSaving ? '儲存中...' : '儲存使用者'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [deptCodes, setDeptCodes] = useState([]);
  const heartbeatTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);

  const [showVendorSettingsModal, setShowVendorSettingsModal] = useState(false);
  const [vendorSuccess, setVendorSuccess] = useState('');
  const [vendorError, setVendorError] = useState('');
  const [maxOnlineUsers, setMaxOnlineUsers] = useState(1);
  const [currentOnlineUsers, setCurrentOnlineUsers] = useState(0);
  const [vendorLoading, setVendorLoading] = useState(false);

  const [showHiddenAdminLogin, setShowHiddenAdminLogin] = useState(false);
  const [hiddenAdminPassword, setHiddenAdminPassword] = useState('');
  const [hiddenAdminError, setHiddenAdminError] = useState('');

  const [showCustomerPermissionModal, setShowCustomerPermissionModal] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [works, setWorks] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [adminOnlineMap, setAdminOnlineMap] = useState({});

  const buildOnlineStatusMap = (users = [], data = {}) => {
    const rawMap =
      data?.onlineStatusMap ||
      data?.onlineUsers ||
      data?.userOnlineStatus ||
      data?.onlineMap ||
      {};

    const rawSessions =
      data?.onlineSessions ||
      data?.sessions ||
      data?.activeSessions ||
      [];

    const nextMap = {};

    if (rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap)) {
      Object.entries(rawMap).forEach(([key, value]) => {
        const normalizedKey = String(key || '').trim().toUpperCase();
        if (!normalizedKey) return;

        if (typeof value === 'boolean') {
          nextMap[normalizedKey] = value;
          return;
        }

        if (typeof value === 'string') {
          nextMap[normalizedKey] = ['online', 'active', '1', 'true', '上線'].includes(
            value.toLowerCase()
          );
          return;
        }

        const nestedStatus = String(value?.status || value?.state || '').toLowerCase();
        nextMap[normalizedKey] = Boolean(
          value?.isOnline ??
          value?.online ??
          value?.active ??
          ['online', 'active', '上線'].includes(nestedStatus)
        );
      });
    }

    if (Array.isArray(rawSessions)) {
      rawSessions.forEach((session) => {
        const sessionUserId = String(
          session?.userId || session?.username || session?.account || ''
        )
          .trim()
          .toUpperCase();

        if (sessionUserId) {
          nextMap[sessionUserId] = true;
        }
      });
    }

    users.forEach((item) => {
      const normalizedUserId = String(item?.userId || '')
        .trim()
        .toUpperCase();

      if (!normalizedUserId) return;

      if (typeof item?.isOnline === 'boolean') {
        nextMap[normalizedUserId] = item.isOnline;
        return;
      }

      if (typeof item?.online === 'boolean') {
        nextMap[normalizedUserId] = item.online;
        return;
      }

      const statusText = String(item?.status || item?.onlineStatus || '').toLowerCase();
      if (statusText) {
        nextMap[normalizedUserId] = ['online', 'active', '上線'].includes(statusText);
        return;
      }

      if (typeof nextMap[normalizedUserId] !== 'boolean') {
        nextMap[normalizedUserId] = false;
      }
    });

    return nextMap;
  };

  const countOnlineUsers = (statusMap = {}) =>
    Object.values(statusMap).filter(Boolean).length;

  const isRoot = (user?.username || user?.id || '').toUpperCase() === 'ROOT';

  const selectedUser = useMemo(
    () => adminUsers.find((item) => item.userId === selectedUserId) || null,
    [adminUsers, selectedUserId]
  );

  useEffect(() => {
    if (!selectedUser) return;
    setForm({
      userId: selectedUser.userId || '',
      name: selectedUser.name || '',
      password: selectedUser.password || '',
      permissions: Array.isArray(selectedUser.permissions) ? selectedUser.permissions : [],
      deptCodes: Array.isArray(selectedUser.deptCodes) ? selectedUser.deptCodes : [],
    });
  }, [selectedUser]);

  const clearLoginState = () => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('permissions');
    localStorage.removeItem('deptCodes');
    localStorage.removeItem('portalSessionId');
    localStorage.removeItem('portalTokenJti');
    localStorage.removeItem('portalLeaveTime');
    setToken('');
    setUser(null);
    setPermissions([]);
    setDeptCodes([]);
    setUsername('');
    setPassword('');
    setError('');
    setShowCustomerPermissionModal(false);
    setAdminUsers([]);
    setAdminOnlineMap({});
    setWorks([]);
    setSelectedUserId('');
    setForm(EMPTY_FORM);
  };

  const saveLoginState = (data) => {
    const nextToken = data.token || '';
    const nextUser = data.user || null;
    const nextPermissions = data.permissions || [];
    const nextDeptCodes = data.deptCodes || [];
    const jwt = parseJwt(nextToken);
    const sessionId = data.sessionId || jwt?.sid || '';
    const tokenJti = data.tokenJti || jwt?.jti || '';

    localStorage.setItem('portalToken', nextToken);
    localStorage.setItem('portalUser', JSON.stringify(nextUser));
    localStorage.setItem('permissions', JSON.stringify(nextPermissions));
    localStorage.setItem('deptCodes', JSON.stringify(nextDeptCodes));
    if (sessionId) localStorage.setItem('portalSessionId', sessionId);
    if (tokenJti) localStorage.setItem('portalTokenJti', tokenJti);

    setToken(nextToken);
    setUser(nextUser);
    setPermissions(nextPermissions);
    setDeptCodes(nextDeptCodes);
  };

  const sendHeartbeat = async () => {
    const currentToken = localStorage.getItem('portalToken') || '';
    const sessionId = localStorage.getItem('portalSessionId') || '';
    const tokenJti = localStorage.getItem('portalTokenJti') || '';

    if (!currentToken || (!sessionId && !tokenJti)) return;

    try {
      const res = await axios.post(
        HEARTBEAT_API,
        { sessionId, tokenJti },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (res?.data?.success === false) {
        clearLoginState();
        setError('登入狀態已失效，請重新登入');
      }
    } catch (err) {
      console.error('heartbeat failed', err?.response?.data || err.message);
    }
  };

  const callLogoutApi = async ({ useBeacon = false } = {}) => {
    const currentToken = localStorage.getItem('portalToken') || '';
    const sessionId = localStorage.getItem('portalSessionId') || '';
    const tokenJti = localStorage.getItem('portalTokenJti') || '';
    const currentUser = JSON.parse(localStorage.getItem('portalUser') || 'null');
    const payload = {
      sessionId,
      tokenJti,
      userId: currentUser?.username || currentUser?.id || '',
    };

    if (!payload.sessionId && !payload.tokenJti && !payload.userId) return;

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(LOGOUT_API, blob);
      return;
    }

    try {
      await axios.post(LOGOUT_API, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
      });
    } catch (err) {
      console.error('logout api failed', err?.response?.data || err.message);
    }
  };

  const fetchAdminData = async (systemSettings = null) => {
    setAdminError('');
    setAdminSuccess('');
    setAdminLoading(true);

    try {
      const res = await axios.get(ADMIN_DATA_API, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('portalToken') || token}`,
        },
      });

      const data = res.data?.data || {};
      const users = data?.users || [];
      const worksList = data?.works || [];
      const onlineMap = buildOnlineStatusMap(users, data);

      const savedPortalUser = JSON.parse(localStorage.getItem('portalUser') || 'null');
      const currentLoginUserId = String(
        user?.username || user?.id || savedPortalUser?.username || savedPortalUser?.id || ''
      )
        .trim()
        .toUpperCase();

      if (currentLoginUserId) {
        onlineMap[currentLoginUserId] = true;
      }

      setAdminUsers(users);
      setWorks(worksList);
      setAdminOnlineMap(onlineMap);

      const nextMaxOnlineUsers =
        data?.maxOnlineUsers ??
        data?.systemSettings?.maxOnlineUsers ??
        systemSettings?.maxOnlineUsers ??
        maxOnlineUsers;

      const calculatedOnlineCount = countOnlineUsers(onlineMap);
      const nextCurrentOnlineUsers = Math.max(
        currentLoginUserId ? 1 : 0,
        Number(
          data?.currentOnlineUsers ??
          data?.systemSettings?.currentOnlineUsers ??
          systemSettings?.currentOnlineUsers ??
          calculatedOnlineCount
        ) || 0,
        calculatedOnlineCount
      );

      setMaxOnlineUsers(Number(nextMaxOnlineUsers) || 0);
      setCurrentOnlineUsers(nextCurrentOnlineUsers);

      if (users.length > 0) {
        setSelectedUserId(users[0].userId);
      } else {
        setSelectedUserId('');
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      setAdminError(err?.response?.data?.message || '讀取管理資料失敗');
    } finally {
      setAdminLoading(false);
    }
  };

  const openCustomerPermissionModal = async () => {
    setShowCustomerPermissionModal(true);

    let systemSettings = null;
    try {
      const res = await axios.get(SYSTEM_SETTINGS_API);
      systemSettings = res?.data?.data || null;

      if (systemSettings) {
        setMaxOnlineUsers(Number(systemSettings.maxOnlineUsers || 1));
        setCurrentOnlineUsers(Number(systemSettings.currentOnlineUsers || 0));
      }
    } catch (err) {
      console.error('讀取系統設定失敗', err?.response?.data || err.message);
    }

    await fetchAdminData(systemSettings);
  };

  const getHiddenAdminPassword = () => {
    const now = new Date();
    const target = new Date(now);
    target.setDate(target.getDate() + 3);
    const rocYear = String(target.getFullYear() - 1911);
    const month = String(target.getMonth() + 1).padStart(2, '0');
    const day = String(target.getDate()).padStart(2, '0');
    return `Goinfo${rocYear}${month}${day}`;
  };

  const fetchSystemSettings = async () => {
    setVendorError('');
    setVendorSuccess('');
    setVendorLoading(true);
    try {
      const res = await axios.get(SYSTEM_SETTINGS_API);
      const data = res?.data?.data || {};
      setMaxOnlineUsers(Number(data.maxOnlineUsers || 1));
      setCurrentOnlineUsers(Number(data.currentOnlineUsers || 0));
    } catch (err) {
      setVendorError(err?.response?.data?.message || '讀取系統設定失敗');
    } finally {
      setVendorLoading(false);
    }
  };

  const openHiddenAdminLogin = () => {
    setHiddenAdminPassword('');
    setHiddenAdminError('');
    setShowHiddenAdminLogin(true);
  };

  const verifyHiddenAdminPassword = () => {
    const expected = getHiddenAdminPassword();
    if (hiddenAdminPassword.trim() !== expected) {
      setHiddenAdminError('後台密碼錯誤');
      return;
    }

    setShowHiddenAdminLogin(false);
    setHiddenAdminPassword('');
    setHiddenAdminError('');
    setShowVendorSettingsModal(true);
    fetchSystemSettings();
  };

  const handleBlueAreaContextMenu = (e) => {
    e.preventDefault();
    openHiddenAdminLogin();
  };

  const handleBlueAreaTouchStart = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      openHiddenAdminLogin();
    }, 800);
  };

  const clearBlueAreaLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const resetFormForNewUser = () => {
    setSelectedUserId('__new__');
    setAdminError('');
    setAdminSuccess('');
    setForm({ ...EMPTY_FORM });
  };

  const updateFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePermission = (perm) => {
    setForm((prev) => {
      const exists = prev.permissions.includes(perm);
      return {
        ...prev,
        permissions: exists ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm],
      };
    });
  };

  const toggleDeptCode = (deptCode) => {
    setForm((prev) => {
      const exists = prev.deptCodes.includes(deptCode);
      return {
        ...prev,
        deptCodes: exists ? prev.deptCodes.filter((d) => d !== deptCode) : [...prev.deptCodes, deptCode],
      };
    });
  };

  const handleSaveUser = async () => {
    setAdminError('');
    setAdminSuccess('');
    if (!form.userId.trim()) {
      setAdminError('請輸入帳號');
      return;
    }
    if (!form.name.trim()) {
      setAdminError('請輸入姓名');
      return;
    }
    if (!form.password.trim()) {
      setAdminError('請輸入密碼');
      return;
    }
    if (form.permissions.length === 0) {
      setAdminError('請至少勾選一個系統權限');
      return;
    }

    setAdminSaving(true);
    try {
      const payload = {
        userId: form.userId.trim().toUpperCase(),
        name: form.name.trim(),
        password: form.password,
        permissions: form.permissions,
        deptCodes: form.deptCodes,
      };

      const res = await axios.post(SAVE_USER_API, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('portalToken') || token}`,
        },
      });

      if (res.data?.success) {
        setAdminSuccess(res.data?.message || '儲存成功');
        await fetchAdminData();
        setSelectedUserId(payload.userId);
      } else {
        setAdminError(res.data?.message || '儲存失敗');
      }
    } catch (err) {
      setAdminError(err?.response?.data?.message || '儲存失敗');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleSaveVendorSettings = async () => {
    setVendorError('');
    setVendorSuccess('');
    setVendorLoading(true);
    try {
      const res = await axios.post(SAVE_SYSTEM_SETTINGS_API, { maxOnlineUsers }, { headers: { 'Content-Type': 'application/json' } });
      if (res.data?.success) {
        setVendorSuccess(res.data?.message || `最大同時登入人數已設定為 ${maxOnlineUsers} 人`);
        setCurrentOnlineUsers(Number(res.data?.data?.currentOnlineUsers || currentOnlineUsers));
      } else {
        setVendorError(res.data?.message || '儲存系統設定失敗');
      }
    } catch (err) {
      setVendorError(err?.response?.data?.message || '儲存系統設定失敗');
    } finally {
      setVendorLoading(false);
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const savedToken = localStorage.getItem('portalToken');
    const savedUser = localStorage.getItem('portalUser');
    const savedPermissions = localStorage.getItem('permissions');
    const savedDeptCodes = localStorage.getItem('deptCodes');

    if (savedToken) setToken(savedToken);
    if (savedUser) setUser(JSON.parse(savedUser));
    if (savedPermissions) setPermissions(JSON.parse(savedPermissions));
    if (savedDeptCodes) setDeptCodes(JSON.parse(savedDeptCodes));

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return undefined;
    }

    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, 60 * 1000);

    const handleUnload = () => {
      if (!window.isNavigatingToSystem && localStorage.getItem('portalToken')) {
        callLogoutApi({ useBeacon: true });
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [token]);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!LOGIN_API) {
        setTimeout(() => {
          const mockData = {
            success: true,
            token: 'mock-token-123',
            sessionId: 'mock-session-id',
            tokenJti: 'mock-token-jti',
            user: { username: username, displayName: '測試人員' },
            permissions: ['AHMShi', 'AHMShr'],
            deptCodes: ['建案A', '建案B'],
          };
          saveLoginState(mockData);
          setLoading(false);
        }, 1000);
        return;
      }

      const res = await axios.post(
        LOGIN_API,
        { username, password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (res.data.success) {
        saveLoginState(res.data);
      } else {
        setError(res.data.message || '登入失敗');
      }
    } catch (err) {
      setError(err?.response?.data?.message || '登入失敗，請檢查網路連線或系統設定');
    } finally {
      if (LOGIN_API) setLoading(false);
    }
  };

  const goAHMShi = () => {
    window.isNavigatingToSystem = true;
    const currentToken = localStorage.getItem('portalToken') || '';
    const currentUsername = user?.username || '';
    const currentName = user?.displayName || currentUsername;
    const projects = (deptCodes || []).join(',');
    window.location.href = `${AHMShi_URL}?token=${encodeURIComponent(currentToken)}&username=${encodeURIComponent(currentUsername)}&name=${encodeURIComponent(currentName)}&projects=${encodeURIComponent(projects)}`;
  };

  const goAHMShr = () => {
    window.isNavigatingToSystem = true;
    const currentToken = localStorage.getItem('portalToken') || '';
    const currentUsername = user?.username || '';
    const currentName = user?.displayName || currentUsername;
    const projects = (deptCodes || []).join(',');
    window.location.href = `${AHMShr_URL}?token=${encodeURIComponent(currentToken)}&username=${encodeURIComponent(currentUsername)}&name=${encodeURIComponent(currentName)}&projects=${encodeURIComponent(projects)}`;
  };

  const logout = async () => {
    window.isNavigatingToSystem = true;
    await callLogoutApi();
    clearLoginState();
  };

  const canUseAHMShi = permissions.includes('AHMShi');
  const canUseAHMShr = permissions.includes('AHMSbr') || permissions.includes('AHMShr');

  if (!token) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] p-4 font-['Noto_Sans_TC']">
          <div className="w-full max-w-[420px] bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col">
            <div
              className="bg-[#2c63f0] pt-12 pb-8 px-6 text-center relative flex-shrink-0 select-none"
              onContextMenu={handleBlueAreaContextMenu}
              onTouchStart={handleBlueAreaTouchStart}
              onTouchEnd={clearBlueAreaLongPress}
              onTouchMove={clearBlueAreaLongPress}
            >
              <div className="flex justify-center items-center gap-6 mb-6">
                <div className="w-[84px] h-[84px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm shadow-inner">
                  <Home size={40} className="text-white" strokeWidth={2} />
                </div>
                <div className="w-[84px] h-[84px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm shadow-inner">
                  <Wrench size={40} className="text-white" strokeWidth={2} />
                </div>
              </div>
              <h1 className="text-white text-[22px] font-bold tracking-wider mb-2 drop-shadow-md">AHMSir房屋驗收暨報修管理系統</h1>
              <div className="flex items-center justify-center gap-2 text-white/90 text-base mb-1">
                <span>請登入以存取系統</span>
              </div>
            </div>

            <div className="p-8 bg-white flex-grow">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <label className="flex items-center text-[#3b4c6b] text-sm font-medium">
                    <User size={16} className="mr-2 text-[#4f678f]" />
                    帳號 (Account)
                  </label>
                  <input
                    type="text"
                    placeholder="請輸入使用者帳號"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0] transition-colors placeholder:text-gray-400"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center text-[#3b4c6b] text-sm font-medium">
                    <Lock size={16} className="mr-2 text-[#4f678f]" />
                    密碼 (Password)
                  </label>
                  <input
                    type="password"
                    placeholder="請輸入密碼"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0] transition-colors placeholder:text-gray-400"
                    required
                  />
                </div>

                {error && <div className="text-red-500 text-sm text-center bg-red-50 py-2 rounded-md">{error}</div>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2c63f0] hover:bg-[#1e4ed8] text-white py-3.5 rounded-xl font-medium flex items-center justify-center transition-colors shadow-md disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? '登入中...' : <><LogIn size={20} className="mr-2" />登入系統</>}
                </button>
              </form>
            </div>
          </div>
        </div>

        {showHiddenAdminLogin && (
          <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <div className="text-[20px] font-bold text-[#1f2937]">公司維運後台驗證</div>
                  <p className="text-sm text-gray-500 mt-1">輸入正確密碼後，直接進入同時上線人數設定</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowHiddenAdminLogin(false);
                    setHiddenAdminPassword('');
                    setHiddenAdminError('');
                  }}
                  className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6">
                <label className="block text-sm font-medium text-[#3b4c6b] mb-2">後台密碼</label>
                <input
                  type="password"
                  value={hiddenAdminPassword}
                  onChange={(e) => setHiddenAdminPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#fbfbfb] text-gray-800 focus:outline-none focus:border-[#2c63f0] focus:ring-1 focus:ring-[#2c63f0]"
                  placeholder="請輸入後台密碼"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') verifyHiddenAdminPassword();
                  }}
                />
                {hiddenAdminError && <div className="mt-3 text-red-600 text-sm bg-red-50 border border-red-100 rounded-xl px-4 py-3">{hiddenAdminError}</div>}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHiddenAdminLogin(false);
                      setHiddenAdminPassword('');
                      setHiddenAdminError('');
                    }}
                    className="px-5 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={verifyHiddenAdminPassword}
                    className="px-5 py-3 rounded-xl bg-[#2c63f0] hover:bg-[#1e4ed8] text-white font-medium transition-colors"
                  >
                    進入後台
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <VendorSettingsModal
          visible={showVendorSettingsModal}
          onClose={() => setShowVendorSettingsModal(false)}
          maxOnlineUsers={maxOnlineUsers}
          setMaxOnlineUsers={setMaxOnlineUsers}
          currentOnlineUsers={currentOnlineUsers}
          setCurrentOnlineUsers={setCurrentOnlineUsers}
          vendorSuccess={vendorSuccess}
          vendorError={vendorError}
          onSave={handleSaveVendorSettings}
          vendorLoading={vendorLoading}
        />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] p-4 font-['Noto_Sans_TC']">
        <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl overflow-hidden relative">
          <div className="bg-[#2c63f0] h-[88px] w-full"></div>
          <div className="absolute top-[56px] left-1/2 -translate-x-1/2">
            <div className="w-[64px] h-[64px] bg-[#eef4ff] rounded-full flex items-center justify-center shadow-sm">
              <User size={32} className="text-[#2c63f0]" />
            </div>
          </div>

          <div className="pt-10 pb-8 px-6">
            <div className="text-center mb-6">
              <h2 className="text-[22px] font-bold text-black tracking-wide"><span className="text-green-800">{user?.displayName}</span> 歡迎登入</h2>
              <p className="text-[18px] font-bold text-black mt-1">請選擇您要進入的系統</p>
            </div>

            {isRoot && (
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={openCustomerPermissionModal}
                  className="inline-flex items-center gap-2 bg-[#eef3ff] text-[#2c63f0] hover:bg-[#dfe9ff] px-4 py-2 rounded-xl text-sm font-bold transition-colors shadow-sm"
                >
                  <Settings size={16} />
                  權限設定
                </button>
              </div>
            )}

            <div className="space-y-4">
              {canUseAHMShi && (
                <div className="border border-gray-100 rounded-xl bg-white shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col overflow-hidden" onClick={goAHMShi}>
                  <div className="p-4 flex-grow">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="w-12 h-12 bg-[#e0e7ff] rounded-xl flex items-center justify-center shrink-0"><Building2 size={24} className="text-[#2563eb]" /></div>
                      <h3 className="text-[18px] font-bold text-black tracking-wide">房屋驗收管理系統</h3>
                    </div>
                    <p className="text-[13px] text-gray-500 leading-relaxed font-medium">AHMShi 提供新建案驗屋進度追蹤、缺失紀錄與修繕派工管理。</p>
                  </div>
                  <div className="border-t border-gray-50 px-4 py-3 flex items-center justify-between text-[#2563eb] hover:bg-gray-50 transition-colors">
                    <span className="text-[13px] font-medium tracking-wide">進入系統 (AHMShi)</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              )}

              {canUseAHMShr && (
                <div className="border border-gray-100 rounded-xl bg-white shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col overflow-hidden" onClick={goAHMShr}>
                  <div className="p-4 flex-grow">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="w-12 h-12 bg-[#ffedd5] rounded-xl flex items-center justify-center shrink-0"><Wrench size={24} className="text-[#ea580c]" /></div>
                      <h3 className="text-[18px] font-bold text-black tracking-wide">房屋報修管理系統</h3>
                    </div>
                    <p className="text-[13px] text-gray-500 leading-relaxed font-medium">AHMShr - 提供交屋後住戶報修申請、客服派單與維修進度管理。</p>
                  </div>
                  <div className="border-t border-gray-50 px-4 py-3 flex items-center justify-between text-[#ea580c] hover:bg-gray-50 transition-colors">
                    <span className="text-[13px] font-medium tracking-wide">進入系統 (AHMShr)</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              )}

              {!canUseAHMShi && !canUseAHMShr && <div className="text-red-500 text-center text-sm py-4 bg-red-50 rounded-xl border border-red-100">您目前沒有可使用的系統權限</div>}

              <button className="w-full mt-2 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 py-3 rounded-xl text-[15px] font-medium transition-colors" onClick={logout}>登出</button>
            </div>
          </div>
        </div>
      </div>

      <CustomerPermissionModal
        visible={showCustomerPermissionModal}
        onClose={() => setShowCustomerPermissionModal(false)}
        adminLoading={adminLoading}
        adminSaving={adminSaving}
        adminError={adminError}
        adminSuccess={adminSuccess}
        adminUsers={adminUsers}
        selectedUserId={selectedUserId}
        setSelectedUserId={setSelectedUserId}
        resetFormForNewUser={resetFormForNewUser}
        form={form}
        updateFormField={updateFormField}
        togglePermission={togglePermission}
        works={works}
        toggleDeptCode={toggleDeptCode}
        handleSaveUser={handleSaveUser}
        currentOnlineUsers={currentOnlineUsers}
        maxOnlineUsers={maxOnlineUsers}
        onlineStatusMap={adminOnlineMap}
      />
    </>
  );
}
